import React, { useEffect } from 'react';
import { array, func } from 'prop-types';
import { connect, useDispatch } from 'react-redux';
import { Box, Flex } from 'rebass';
import { Formik } from 'formik';
import { object, string } from 'yup';
import join from 'url-join';
import { ButtonStyled, DropDownStyled, TextAreaStyled } from '@nike/nike-design-system-components';

import http from '~APP_UTIL/http';
import { isValidProductCode } from '../utils';
import { handleErrorNoAction } from '../../util';

import { fetchSeasons } from '../actions';
import { showToast } from '../../../components/toast/actions';

import { getSeasons } from '../selectors';

import { SALES_ORGS } from '../constants';
import BulkDeleteModal from './BulkDeleteModal';

import useModal from '../../../hooks/useModal';

const api2 = join(import.meta.env.VITE_API2_URL, 'v1');

const validationSchema = object().shape({
  season: string().nullable(),
  salesOrg: string().nullable(),
  productCodes: string()
    .required('Please enter a list of product codes')
    .test(
      'areProductCodesValid',
      // eslint-disable-next-line no-template-curly-in-string
      'Product codes not valid',
      value =>
        value
          ?.trim()
          .split(/[,|.| |\n]+/)
          .filter(Boolean)
          .every(productCode => isValidProductCode(productCode))
    )
});

const BulkDeleteProducts = ({ seasons, fetchSeasons }) => {
  const [isBulkDeleteModalOpen, openBulkDeleteModal, closeBulkDeleteModal] = useModal();

  const dispatch = useDispatch();

  const seasonOptions = seasons.map(season => ({ label: season.name, value: season.name }));

  const formInitialValues = {
    season: null,
    salesOrg: null,
    productCodes: ''
  };

  useEffect(() => {
    fetchSeasons();
  }, []);

  const submit = ({ productCodes, salesOrg, season }, { resetForm }) => {
    const payload = {
      productCodes: productCodes.split(/[,|.| |\n]+/),
      ...(season && { seasons: [season] }),
      ...(salesOrg && { salesOrgs: [salesOrg] })
    };

    resetForm({});
    http
      .post(join(api2, '/catalogProducts/bulkDeleteProducts'), payload)
      .then(() => {
        dispatch(
          showToast(
            {
              type: 'success',
              message: 'Product Codes deleted'
            },
            3000
          )
        );
      })
      .catch(error => handleErrorNoAction(dispatch, error));
  };

  return (
    <Formik initialValues={formInitialValues} onSubmit={submit} validationSchema={validationSchema} enableReinitialize>
      {({
        values,
        handleChange,
        handleSubmit,
        handleBlur,
        isValid,
        dirty,
        isSubmitting,
        errors,
        touched,
        setFieldValue
      }) => {
        const isSubmitButtonDisabled = !isValid || !dirty || isSubmitting;

        return (
          <>
            <Flex flexDirection='row' mt='20px' width='100%'>
              <Box width='275px'>
                <DropDownStyled
                  id='season'
                  name='season'
                  label='Select Season'
                  options={seasonOptions}
                  value={values.season}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
              </Box>
              <Box ml='24px' width='275px'>
                <DropDownStyled
                  id='salesOrg'
                  name='salesOrg'
                  label='Select Sales Org'
                  options={SALES_ORGS}
                  value={values.salesOrg}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
              </Box>
            </Flex>
            {/* Height given on Box to account for TextArea focus height change */}
            <Box height='375px'>
              <TextAreaStyled
                id='productCodes'
                name='productCodes'
                minHeight={350}
                label='Product Codes to Delete'
                supportText='Separated by spaces'
                onChange={handleChange}
                value={values.productCodes}
                onBlur={event => {
                  setFieldValue('productCodes', values.productCodes.replace(/\n/g, ','));
                  handleBlur(event);
                }}
                error={touched.productCodes && errors.productCodes}
                errorMessage='Invalid product code'
              />
            </Box>
            <Flex justifyContent='flex-end' mt='20px'>
              <ButtonStyled disabled={isSubmitButtonDisabled} onClick={openBulkDeleteModal}>
                Delete
              </ButtonStyled>
            </Flex>
            <BulkDeleteModal
              isOpen={isBulkDeleteModalOpen}
              closeModal={closeBulkDeleteModal}
              triggerDelete={handleSubmit}
            />
          </>
        );
      }}
    </Formik>
  );
};

BulkDeleteProducts.propTypes = {
  seasons: array.isRequired,
  fetchSeasons: func.isRequired
};

const mapStateToProps = state => {
  return {
    seasons: getSeasons(state)
  };
};

export default connect(mapStateToProps, { fetchSeasons })(BulkDeleteProducts);
